import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Save, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;
const isSuccess = (response) => response === true || response === 'true' || response === 'Success';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB');
};

const UpdateStoreRefNo = () => {
  const [statuses, setStatuses] = useState([]);
  const [rows, setRows] = useState([]);
  const [statusId, setStatusId] = useState('');
  const [appliedStatusId, setAppliedStatusId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [editedRefs, setEditedRefs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const pageSize = viewAll ? 100000 : PAGE_SIZE;
  const totalPages = viewAll ? 1 : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const savePayload = useMemo(
    () => rows.map((row) => ({ Id: row.Id, StoreRefNo: Number(editedRefs[row.Id] ?? row.StoreRefNo) || 0 })),
    [editedRefs, rows]
  );

  const loadStatuses = useCallback(async () => {
    try {
      const response = await MainService.StatusInDropDown();
      setStatuses(parseList(response));
    } catch (err) {
      console.error('Load statuses error:', err);
      setStatuses([]);
      setError('Failed to load statuses');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.OrdersInRefEdit({
        StatusId: Number(appliedStatusId) || 0,
        Skip: viewAll ? 0 : (currentPage - 1) * PAGE_SIZE,
        Take: pageSize,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
      setEditedRefs({});
    } catch (err) {
      console.error('Load orders for ref edit error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Network Error');
    } finally {
      setLoading(false);
    }
  }, [appliedStatusId, currentPage, pageSize, viewAll]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const filterData = () => {
    setAppliedStatusId(statusId);
    setCurrentPage(1);
    setViewAll(false);
  };

  const refreshData = () => {
    setStatusId('');
    setAppliedStatusId('');
    setCurrentPage(1);
    setViewAll(false);
  };

  const updateRefDraft = (orderId, value) => {
    setEditedRefs((prev) => ({ ...prev, [orderId]: value }));
  };

  const saveStoreRefNoList = async () => {
    if (rows.length === 0) {
      setError('No orders to save');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveStoreRefList(savePayload);
      if (isSuccess(response)) {
        setSuccess('Success');
        await loadRows();
      } else {
        setError('Network Error');
      }
    } catch (err) {
      console.error('Save store ref list error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Update Store Ref No</h1>
            <p className="mt-1 text-sm text-gray-500">Filter orders by status and update store reference numbers.</p>
          </div>
          <button
            type="button"
            onClick={refreshData}
            disabled={loading || saving}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-4">
            <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
            <select
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              <option value="">All Statuses</option>
              {statuses.map((status) => (
                <option key={status.Id} value={status.Id}>
                  {status.Name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2 lg:col-span-8">
            <button
              type="button"
              onClick={filterData}
              disabled={loading || saving}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <Search className="h-4 w-4" />
              Filter
            </button>
            <button
              type="button"
              onClick={saveStoreRefNoList}
              disabled={saving || rows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              Save Ref No
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-2 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Orders</h2>
            <p className="text-sm text-gray-500">
              Showing {rows.length} of {totalCount} orders
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1200px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>#</Th>
                <Th>Voucher No</Th>
                <Th>Client Bill</Th>
                <Th>Date</Th>
                <Th>Client</Th>
                <Th>Client Mob</Th>
                <Th>Customer</Th>
                <Th>Customer Mob</Th>
                <Th>Status</Th>
                <Th>Ref No</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={10} text="Loading orders..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={10} text="No orders found." />
              ) : (
                rows.map((row, index) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    <Td>{viewAll ? index + 1 : (currentPage - 1) * PAGE_SIZE + index + 1}</Td>
                    <Td strong>{row.VoucherNo}</Td>
                    <Td>{row.ClientBillNo || '-'}</Td>
                    <Td>{formatDate(row.DeliveryDate)}</Td>
                    <Td>{row.Client?.Name || '-'}</Td>
                    <Td>{row.Client?.MobileNo1 || '-'}</Td>
                    <Td>{row.Customer?.Name || '-'}</Td>
                    <Td>{row.Customer?.MobileNo1 || '-'}</Td>
                    <Td>{row.Status?.Name || '-'}</Td>
                    <Td>
                      <input
                        type="number"
                        value={editedRefs[row.Id] ?? row.StoreRefNo ?? 0}
                        onChange={(event) => updateRefDraft(row.Id, event.target.value)}
                        className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      />
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
          <span>
            Showing {rows.length ? (viewAll ? 1 : (currentPage - 1) * PAGE_SIZE + 1) : 0} to {viewAll ? rows.length : Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} orders
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                setViewAll((value) => !value);
              }}
              disabled={loading}
              className="rounded-lg border border-primary px-3 py-1.5 font-semibold text-primary hover:bg-primary/5 disabled:opacity-50"
            >
              {viewAll ? 'Show Pages' : 'View All'}
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={viewAll || currentPage <= 1 || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={viewAll || currentPage >= totalPages || loading}
              className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children }) => (
  <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
    {children}
  </th>
);

const Td = ({ children, strong = false }) => (
  <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
    {children}
  </td>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">
      {text}
    </td>
  </tr>
);

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default UpdateStoreRefNo;
